import { useMemo, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClientFilesList } from "./ClientFilesList";
import { ACCEPT, BulkUploadDialog } from "./BulkUploadDialog";
import { FileDropZone } from "./FileDropZone";
import { CATEGORY_OPTIONS } from "./fileUtils";

function CategoryChips({ files, value, onChange }) {
  const counts = useMemo(() => files.reduce((m, f) => {
    const k = f.category || "other";
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {}), [files]);
  const chips = [["all", "All", files.length],
    ...CATEGORY_OPTIONS.filter(([k]) => counts[k]).map(([k, l]) => [k, l, counts[k]])];
  if (chips.length <= 2) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="files-category-chips">
      {chips.map(([k, l, n]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`files-chip-${k}`}
                aria-pressed={value === k}
                className={`h-7 px-2.5 rounded-full border text-xs transition-colors ${value === k
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
          {l} <span className={`ml-0.5 font-mono-num ${value === k ? "text-zinc-300" : "text-zinc-400"}`}>{n}</span>
        </button>
      ))}
    </div>
  );
}

export function ClientFilesSection({ email, files, onChanged }) {
  const [cat, setCat] = useState("all");
  const [queued, setQueued] = useState(null);
  const pickRef = useRef(null);
  const active = cat !== "all" && files.some((f) => (f.category || "other") === cat) ? cat : "all";
  const shown = active === "all" ? files : files.filter((f) => (f.category || "other") === active);

  return (
    <FileDropZone as="section" onFiles={setQueued} testId="files-section" overlayTestId="files-drop-overlay"
                  className="space-y-2">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-1.5">
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Files ({files.length})</h3>
        <div className="flex items-center gap-2">
          <span className="hidden md:inline text-2xs text-zinc-400">or drag files here</span>
          <Button size="sm" variant="outline" onClick={() => pickRef.current?.click()} data-testid="files-upload-btn">
            <Upload className="h-3.5 w-3.5 mr-1" /> Upload files
          </Button>
          <input ref={pickRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="files-upload-input"
                 onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) setQueued(fs); }} />
        </div>
      </div>
      <CategoryChips files={files} value={active} onChange={setCat} />
      {files.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-lg"
             data-testid="files-empty">
          No files yet — drop files here or use Upload files.
        </div>
      ) : (
        <ClientFilesList files={shown} onChanged={onChanged} />
      )}
      {queued && (
        <BulkUploadDialog email={email} initialFiles={queued} onClose={() => setQueued(null)} onUploaded={onChanged} />
      )}
    </FileDropZone>
  );
}
